import React from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { useMutation } from 'react-query';
import * as yup from 'yup';

import * as Api from '../api';
import * as Types from '../types';
import { useTranslation } from 'react-i18next';

interface IFormValues extends Types.Form.NewRefund {}

interface IChildren extends FormikProps<IFormValues> {}

interface IProps {
  id: number;
  type: string;
  level: number;
  className?: string;
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  onSettled?: () => void;
  children(props: IChildren): JSX.Element;
}

const NewRefund: React.FC<IProps> = ({ id, type, level, onSuccess, onError, onSettled, children }) => {
  const { t } = useTranslation();

  const mutation = useMutation<any, string, IFormValues, any>(
    async values => {
      const { data } = await Api.NewRefund({ id, values });

      return data && data.data;
    },
    {
      onSuccess,
      onError,
      onSettled
    }
  );

  const validationSchema = yup.object().shape({
    type: yup.string().required(),
    level: yup.number().required(),
    review_sub_region_id: yup.string().when(`level`, {
      is: 3,
      then: yup.string().required(t('required'))
    }),
    review_region_id: yup.string().when(`level`, {
      is: 2 || 3,
      then: yup.string().required(t('required'))
    }),
    card_number: yup.string().when('type', {
      is: 'INDIVIDUAL',
      then: yup.string().min(16, t('card_number_min_16_number')).max(16).required(t('required')),
      otherwise: yup.string().nullable()
    }),
    card_owner: yup.string().when('type', {
      is: 'INDIVIDUAL',
      then: yup.string().min(2, t('card_owner_limit_min_2')).required(t('required')),
      otherwise: yup.string().nullable()
    }),
    bank_mfo: yup.string().min(5).max(5, t('bank_mfo_limit_5')).required(t('required')),
    bank_tin: yup.string().min(9, t('tin_limit_9')).required(t('required')),
    bank_transit_account: yup.string().when('type', {
      is: 'INDIVIDUAL',
      then: yup.string().required(t('required')),
      otherwise: yup.string().nullable()
    }),
    bank_account: yup.string().when('type', {
      is: 'LEGAL_ENTITY',
      then: yup.string().min(5, t('bank_account_limit_min_5')).required(t('required')),
      otherwise: yup.string().nullable()
    }),
    comment: yup.string().min(5, t('comment_limit_min_5')).max(5000, t('comment_limit_max_5000')).required(t('required')),
    phone: yup.string().min(9).required(t('required'))
  });

  const handleSubmit = (values: IFormValues, { setSubmitting }: FormikHelpers<IFormValues>) => {
    setSubmitting(true);
    mutation.mutate(values, {
      onSettled: () => setSubmitting(false)
    });
  };

  return (
    <Formik<IFormValues>
      onSubmit={handleSubmit}
      initialValues={{
        card_number: '',
        card_owner: '',
        bank_tin: '',
        bank_transit_account: '',
        bank_mfo: '',
        comment: '',
        phone: '',
        bank_account: '',
        review_region_id: '',
        review_sub_region_id: '',
        level: level || 1,
        type: type || 'INDIVIDUAL'
      }}
      {...{ validationSchema }}
    >
      {(props: FormikProps<IFormValues>) => <Form>{children(props)}</Form>}
    </Formik>
  );
};

export default NewRefund;
